import { createSelector } from 'reselect';

import { selectExperimentalEnabled, selectFaceliftEnabled, selectTargetPlatform } from '@pressreader/appcore';
import { selectGetConfigValue } from '@pressreader/config';
import { selectCommentsEnabled, selectShareEnabled } from '@pressreader/global-config';
import { selectIsEmbeddedModeEnabled } from '@pressreader/sponsorship-core';
import { TargetPlatform, toBool } from '@pressreader/types';

import { COPY_LEGAL_NOTICE_FEATURE_FLAG, ZOTERO_EXPORT_FEATURE_FLAG } from './constants';

export const selectImagesConfig = createSelector(selectTargetPlatform, selectGetConfigValue, (platform, getConfigValue) => ({
    imageServers: {
        res: getConfigValue<string | null>('baseUrls.images', null),
        imageDownload: getConfigValue<string | null>('imageServer.download.url', null),
        thumbs: getConfigValue<(string | null)[]>('imageServers.thumbs', [null])[0],
    },
    imagesSupported: platform !== TargetPlatform.Pervasive,
}));

export const selectArticleFeaturesConfig = createSelector(
    selectTargetPlatform,
    selectGetConfigValue,
    selectFaceliftEnabled,
    selectExperimentalEnabled,
    selectShareEnabled,
    selectCommentsEnabled,
    selectIsEmbeddedModeEnabled,
    (platform, getConfigValue, faceliftEnabled, experimentalEnabled, shareEnabled, articleCommentEnabled, isEmbeddedModeEnabled) => {
        const isVoyager = platform === TargetPlatform.Voyager;

        const saveToCollectionEnabled =
            getConfigValue<boolean>('ui.Bookmarks.enable', true) && getConfigValue<boolean>('features.save.to.collection.enabled', true);

        const articleTranslateEnabled =
            getConfigValue<boolean>('ui.Translate.enable', true) && (getConfigValue<boolean>('features.translate.enabled') ?? true);

        const articlePrintEnabled = getConfigValue<boolean>('ui.Print.enable', true) && (getConfigValue<boolean>('features.print.enabled') ?? true);

        const votingEnabled =
            !isEmbeddedModeEnabled &&
            !isVoyager &&
            getConfigValue<boolean>('ui.Voting.enable', true) &&
            getConfigValue('features.vote.enabled', true);

        const textViewButtonEnabled = !faceliftEnabled;

        const articleSummaryEnabled = Boolean(faceliftEnabled && experimentalEnabled);

        const articleSocialEnabled = articleCommentEnabled || votingEnabled;

        const articleCopyEnabled =
            getConfigValue<boolean>('ui.Copy.enable', true) && getConfigValue<boolean>('features.copy.enabled', true) && !isEmbeddedModeEnabled;

        // Opt-in per host: the notice is publisher legal text, so it stays off everywhere until a host
        // turns it on and supplies the wording through the resource bundle.
        const articleCopyLegalNoticeEnabled = getConfigValue<boolean>(COPY_LEGAL_NOTICE_FEATURE_FLAG, false);

        // Exporting to Zotero means handing the page to a browser extension, which an embedded/sponsored
        // surface cannot host — hence the same exclusions the other extension-ish tools carry.
        const articleZoteroExportEnabled = !isEmbeddedModeEnabled && !isVoyager && getConfigValue<boolean>(ZOTERO_EXPORT_FEATURE_FLAG, false);

        return {
            articleCommentEnabled,
            saveToCollectionEnabled,
            articleTranslateEnabled,
            articlePrintEnabled,
            articleShareEnabled: shareEnabled,
            votingEnabled,
            textViewButtonEnabled,
            articleSummaryEnabled,
            articleSocialEnabled,
            articleCopyEnabled,
            articleCopyLegalNoticeEnabled,
            articleZoteroExportEnabled,
        };
    },
);

export const selectArticleTranslationConfig = createSelector(selectGetConfigValue, getConfigValue => {
    const translationConfigDto = getConfigValue<{
        enableSingleArticleTranslation: {
            enableForAnonymous: boolean | string;
            enableForSponsored: boolean | string;
            enableForPremium: boolean | string;
            enableForRegistered: boolean | string;
            value: boolean | string;
        };
    }>('translation');
    return {
        enableForAnon: toBool(translationConfigDto?.enableSingleArticleTranslation.enableForAnonymous),
        enableForSponsored: toBool(translationConfigDto?.enableSingleArticleTranslation.enableForSponsored),
        enableForRegisteredPayG: toBool(translationConfigDto?.enableSingleArticleTranslation.enableForRegistered),
        enableForPremium: toBool(translationConfigDto?.enableSingleArticleTranslation.enableForPremium),
        enable: toBool(translationConfigDto?.enableSingleArticleTranslation.value),
    };
});

export const selectIsContextMenuEnabled = createSelector(selectTargetPlatform, platform => platform !== TargetPlatform.Voyager);
